import { omitUndefined } from '@shared/helpers/object.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import {
  FactoryStorey,
  Storey,
  STOREY_MIN_HEIGHT,
} from '@shared/models/design/storey.model';
import { cloneDeep } from 'lodash';
import { v4 } from 'uuid';
import { createShapes } from './shape.factory';
import { createActivityReferences } from './activity-reference.factory';

/**
 * Create a new storey
 * @param base
 * @returns
 */
export const createStorey = (
  base: FactoryStorey = {},
  regenerateId = true,
): Storey => {
  return {
    ...omitUndefined(cloneDeep(base)), // Make sure activities and shapes are not a clone ref
    activities: createActivityReferences(base.activities), // Make sure we create new ids for all activity references
    height: Math.max(STOREY_MIN_HEIGHT, base.height ?? 3),
    shapes: createShapes(base.shapes), // Use this to make sure ids are regenerated
    id: regenerateId || !base.id ? v4() : base.id, // Allow us to keep ids if provided
  };
};

/**
 * Create multiple storeys. Always create at least one storey if no storeys values are provided.
 * @param base - The base storeys to create.
 * @returns The created storeys.
 */
export const createStoreys = (
  base: Parameters<typeof createStorey>[0][] = [],
  regenerateIds?: boolean,
): Storey[] => {
  const definedBases = base.filter(guards.is.defined);
  if (definedBases.length === 0) {
    return [createStorey()];
  }

  return definedBases.map((b) => createStorey(b, regenerateIds));
};
